import type {
  FormFieldSchema,
  FormSectionSchema,
  SchemaRule,
} from '@tasama/types';
import { resolveFieldState } from './conditions';
import { isGroupRule } from './rule-guards';
import { fieldComparisonMatches, isFieldFilled } from './rule-evaluation.util';
import { stripFieldLabelHtml } from './strip-field-label-html.util';

/**
 * One reason the form cannot be submitted yet, in a shape that serves both consumers:
 * the Zod `superRefine` that keeps SUBMIT disabled, and the summary panel that tells the
 * user why. Deriving both from one pass is the point - anything the panel omits would be
 * an invisible blocker, which is the defect this exists to close (TDF-1410).
 */
export interface MissingRequirement {
  /** Stable identity - React key for the summary row, and unique per blocker. */
  key: string;
  /**
   * What the user reads. A field/section label is the CMS-localized, HTML-stripped one the
   * form itself renders; a group rule contributes its own message instead, because the rule
   * is a statement about several fields at once and no single label describes it.
   */
  label: string;
  /** Form value paths the matching validation issue attaches to. */
  paths: string[];
  /** Message raised against every `paths` entry when this becomes a Zod issue. */
  message: string;
}

/**
 * Every currently unmet requirement, in the order the form renders them: sections in schema
 * order, fields within a section by their BE-provided `order`, cross-field group rules last.
 *
 * Conditional state is respected exactly as the renderer applies it - a field hidden by a
 * rule, or one whose REQUIRE condition is not currently met, is not a blocker and must not
 * be named. `fields` is the caller's already-flattened, renderable field list, so this stays
 * free of any dependency on the Zod schema builder that also calls it.
 */
export function collectMissingRequirements(
  sections: FormSectionSchema[],
  fields: FormFieldSchema[],
  rules: SchemaRule[],
  values: Record<string, unknown>,
  t: (_key: string) => string
): MissingRequirement[] {
  const requiredMessage = t(
    'services.activityRequest.validation.fieldRequired'
  );
  const fieldsByName = new Map(fields.map((field) => [field.name, field]));
  const missing: MissingRequirement[] = [];

  // A name absent from `fieldsByName` isn't a field this pass ever renders — it was dropped
  // upstream (statically hidden, or a caller-side exclusion like NewTicketDrawer's
  // excludeDuplicateFields) — so a group rule referencing it must not treat it as a visible,
  // never-fillable blocker. Without this, such a rule (e.g. REQUIRE_ANY_OF naming an excluded
  // field) can never be satisfied and permanently disables SUBMIT.
  const isHidden = (name: string): boolean => {
    const field = fieldsByName.get(name);
    if (!field) return true;
    return (
      field.hidden === true || resolveFieldState(name, rules, values).hidden
    );
  };

  sections.forEach((section) => {
    // A multi-row set (MRVS) is a section, not a field, so the field loop below never sees it.
    // Its required-ness comes only from a rule targeting the section key - `resolveFieldState`
    // evaluates that rule's conditions and honours a later OPTIONAL, which a bare
    // "does a REQUIRE rule exist" test would not.
    if (section.multiRow) {
      const state = resolveFieldState(section.key, rules, values);
      if (state.hidden || !state.required) return;
      const rows = values[section.key];
      if (Array.isArray(rows) && rows.length > 0) return;
      missing.push({
        key: section.key,
        label: stripFieldLabelHtml(section.label) || section.key,
        paths: [section.key],
        message: requiredMessage,
      });
      return;
    }

    // `order` is authoritative, not array position (see FormFieldSchema) - sorting by it makes
    // the summary read in the same top-to-bottom order as the fields on screen.
    [...(section.fields ?? [])]
      .filter((field) => fieldsByName.has(field.name))
      .sort((a, b) => a.order - b.order)
      .forEach((field) => {
        const state = resolveFieldState(field.name, rules, values);
        if (field.hidden || state.hidden) return;
        if (!field.mandatory && !state.required) return;
        if (isFieldFilled(values[field.name])) return;
        missing.push({
          key: field.name,
          label: stripFieldLabelHtml(state.label ?? field.label) || field.name,
          paths: [field.name],
          message: requiredMessage,
        });
      });
  });

  rules.filter(isGroupRule).forEach((rule, index) => {
    const visibleFields = rule.fields.filter((name) => !isHidden(name));
    if (visibleFields.length === 0) return;

    const isViolated =
      rule.effect === 'REQUIRE_ANY_OF'
        ? !visibleFields.some((name) => isFieldFilled(values[name]))
        : rule.invalidWhen.some((condition) =>
            fieldComparisonMatches(condition, values)
          );
    if (!isViolated) return;

    missing.push({
      key: `${rule.effect}:${index}`,
      // Already localized: the BFF resolves the rule's `messageKey` against the CMS using the
      // request's Accept-Language, so it is shown verbatim and never re-translated.
      label: rule.message,
      paths: visibleFields,
      message: rule.message,
    });
  });

  return missing;
}
